<script setup lang="ts">
import { computed } from 'vue'
import draggable from 'vuedraggable'

import IconPages from '@/components/atoms/icons/IconPages.vue'
import IconPlus from '@/components/atoms/icons/IconPlus.vue'
import BetaNavigationDisclosure from '@/components/beta-dashboard/molecules/BetaNavigationDisclosure.vue'
import BetaPageNavigationItem from '@/components/beta-dashboard/molecules/BetaPageNavigationItem.vue'
import { useBetaDashboardStore } from '@/stores/beta-dashboard'
import type { BetaPage } from '@/types/beta-dashboard'

const props = defineProps<{
    pages: BetaPage[]
    active?: boolean
    selectedPageId?: number | null
}>()

const emit = defineEmits<{
    browse: []
    create: []
    select: [page: BetaPage]
    configure: [page: BetaPage]
}>()

const betaStore = useBetaDashboardStore()
const home = computed(() => (
    props.pages.find((page) => page.isHome)
    ?? props.pages.find((page) => page.id === betaStore.homePage?.id)
))
const sortablePages = computed(() => props.pages.filter((page) => page.id !== home.value?.id))
const orderDisabled = computed(() => betaStore.loadingContent || betaStore.reorderingPages)

const reorderPages = (pages: BetaPage[]) => {
    if (orderDisabled.value) return
    void betaStore.reorderPages(home.value ? [home.value, ...pages] : pages)
}
</script>

<template>
    <BetaNavigationDisclosure label="Páginas" :icon="IconPages" :active="active">
        <template #items>
            <button
                type="button"
                :class="['beta-pages-navigation__shortcut', active && !selectedPageId && 'is-current']"
                :aria-current="active && !selectedPageId ? 'page' : undefined"
                @click="emit('browse')"
            >
                <IconPages aria-hidden="true" />
                <span>Todas as páginas</span>
            </button>

            <BetaPageNavigationItem
                v-if="home"
                :page="home"
                :current="active && home.id === selectedPageId"
                locked
                @select="emit('select', $event)"
                @configure="emit('configure', $event)"
            />

            <draggable
                :model-value="sortablePages"
                item-key="id"
                handle=".handle-drag"
                :disabled="orderDisabled"
                :animation="150"
                class="beta-pages-navigation__sortable"
                :aria-busy="betaStore.reorderingPages"
                @update:model-value="reorderPages"
            >
                <template #item="{ element }">
                    <BetaPageNavigationItem
                        :page="element"
                        :current="active && element.id === selectedPageId"
                        :order-disabled="orderDisabled"
                        @select="emit('select', $event)"
                        @configure="emit('configure', $event)"
                    />
                </template>
            </draggable>

            <button type="button" class="beta-pages-navigation__shortcut has-separator" @click="emit('create')">
                <IconPlus aria-hidden="true" />
                <span>Nova página</span>
            </button>
        </template>
    </BetaNavigationDisclosure>
</template>

<style scoped>
.beta-pages-navigation__sortable {
    display: grid;
    min-width: 0;
    gap: 2px;
}

.beta-pages-navigation__shortcut {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 8px;
    border-radius: 11px;
    padding: 10px 6px;
    color: #607188;
    font-size: 11px;
    font-weight: 500;
    text-align: left;
}

.beta-pages-navigation__shortcut svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
}

.beta-pages-navigation__shortcut:hover,
.beta-pages-navigation__shortcut.is-current {
    background: color-mix(in srgb, var(--beta-accent) 9%, white);
    color: var(--beta-accent);
}

.beta-pages-navigation__shortcut:focus-visible {
    outline: 2px solid var(--beta-accent);
    outline-offset: 1px;
}
</style>
